import React, { useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { compose } from "redux"

import { Switch, Route, Redirect } from "react-router-dom"

import { loadingAnimationVariants } from "constants/internal"
import { mainSections } from "constants/uri"
import withLoggedInUser from "lib/utils/hoc/withLoggedInUser"

import Loading from "lib/views/Loading/Loading"
import AppRoutes from "App/RoutesApp"

import connect from "./connect"

export const EnvironmentWrapper = ({
    selectEnvironment,
    selectCompany,
    isReady,
    companyEnvironment,
    environmentName,
    isSubdomain,
    hasEnvironmentError,
    hasFetchedUser,
    hasActiveRoles,
    hasInvites,
    match,
}) => {
    const {
        params: { environment: urlEnvironment },
    } = match

    const isCorrectUrlEnv = useMemo(
        () =>
            urlEnvironment &&
            urlEnvironment !== "null" &&
            urlEnvironment !== "undefined",
        [urlEnvironment],
    )
    const isCorrectEnvName = useMemo(
        () =>
            environmentName &&
            environmentName !== "null" &&
            environmentName !== "undefined",
        [environmentName],
    )

    const handleCheckCompany = () => {
        const environmentReady =
            isCorrectEnvName &&
            (urlEnvironment === environmentName ||
                (urlEnvironment === mainSections.editor && isCorrectUrlEnv) ||
                (urlEnvironment === mainSections.player && isCorrectUrlEnv) ||
                (urlEnvironment === mainSections.preview && isCorrectUrlEnv))
        const companyNotReady = environmentName !== companyEnvironment

        if (environmentReady && companyNotReady) {
            selectCompany(environmentName)
        }
    }

    useEffect(() => {
        if (isSubdomain) return

        if (
            urlEnvironment &&
            isCorrectUrlEnv &&
            urlEnvironment !== environmentName
        ) {
            selectEnvironment(urlEnvironment)
        }
    }, [urlEnvironment, isSubdomain, hasFetchedUser, isCorrectUrlEnv])

    useEffect(() => {
        if (!hasFetchedUser) return

        const environmentHasChanged = environmentName !== companyEnvironment

        if (
            (!isSubdomain && environmentHasChanged) ||
            (isSubdomain && !companyEnvironment)
        ) {
            handleCheckCompany()
        }
    }, [isSubdomain, hasFetchedUser, companyEnvironment, environmentName])

    if (
        (hasEnvironmentError || !urlEnvironment || !isCorrectUrlEnv) &&
        (hasActiveRoles || hasInvites)
    ) {
        return <Redirect to="/roles" />
    }

    const allEnvironmentsMatch =
        companyEnvironment === environmentName &&
        (environmentName === urlEnvironment || isSubdomain) &&
        (companyEnvironment === urlEnvironment || isSubdomain)

    return isReady && allEnvironmentsMatch ? (
        <Switch>
            <Route
                path={isSubdomain ? "/" : "/:environment?"}
                component={AppRoutes}
            />
        </Switch>
    ) : (
        <Loading variant={loadingAnimationVariants.secondary} />
    )
}

EnvironmentWrapper.propTypes = {
    match: PropTypes.object.isRequired,
    selectEnvironment: PropTypes.func.isRequired,
    selectCompany: PropTypes.func.isRequired,
    environmentName: PropTypes.string,
    companyEnvironment: PropTypes.string,
    isReady: PropTypes.bool,
    isSubdomain: PropTypes.bool,
    hasFetchedUser: PropTypes.bool,
    hasEnvironmentError: PropTypes.bool,
    hasActiveRoles: PropTypes.bool,
    hasInvites: PropTypes.bool,
}

EnvironmentWrapper.defaultProps = {
    environmentName: null,
    isReady: false,
    hasFetchedUser: false,
    companyEnvironment: null,
    isSubdomain: false,
    hasEnvironmentError: false,
    hasActiveRoles: false,
    hasInvites: false,
}

// eslint-disable-next-line import/no-unused-modules
export default compose(
    withLoggedInUser(),
    connect,
    React.memo,
)(EnvironmentWrapper)
